.page {
  padding: var(--space-6);
  display: grid;
  gap: var(--space-6);
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  /* Safari iOS: la barra inferior del browser no tape la última card */
  padding-bottom: max(var(--space-6), calc(env(safe-area-inset-bottom, 0px) + 5rem));
}

.page > * {
  min-width: 0;
  max-width: 100%;
}

/* Header, título y empty-state usan las primitivas globales
   (.page-header / .h2 / .empty-state de styles/global.css). */

.notSuperadmin {
  padding: var(--space-8);
  text-align: center;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  color: var(--color-text-subtle);
}

/* ── Buttons ──────────────────────────────────────────────────── */

.button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px var(--space-3);
  border-radius: var(--radius-md);
  border: 0;
  background: var(--color-primary);
  color: var(--color-on-primary);
  font: inherit;
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
}

.button:hover { opacity: 0.92; }
.button:disabled { opacity: 0.5; cursor: not-allowed; }

.buttonGhost {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px var(--space-3);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: transparent;
  color: var(--color-text);
  font: inherit;
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.buttonGhost:hover { background: var(--color-bg-subtle); }

.iconButton {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-border);
  background: transparent;
  color: var(--color-text-subtle);
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.iconButton:hover {
  background: var(--color-bg-subtle);
  color: var(--color-text);
}

.rowActions {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

.enterButton {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px var(--space-3);
  border: 1px solid var(--color-primary);
  background: var(--color-primary);
  color: var(--color-on-primary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background-color var(--duration-base) var(--ease-standard);
}

.enterButton:hover {
  background: var(--color-primary-deep);
  border-color: var(--color-primary-deep);
}

.deleteButton {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px var(--space-3);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-danger-border);
  background: transparent;
  color: var(--color-danger);
  font: inherit;
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.deleteButton:hover {
  background: var(--color-danger-soft);
}

.deleteButton:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Toolbar: pestañas de tipo + búsqueda ───────────────────── */

.toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-4);
  flex-wrap: wrap;
  margin-bottom: var(--space-4);
}

/* La barra de subtabs ya trae su propio margin-bottom; dentro del toolbar
   lo anulamos para alinear con la búsqueda. */
.toolbar :global(.subtabs) {
  margin-bottom: 0;
  flex: 1 1 auto;
}

.tabCount {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 var(--space-1);
  border-radius: var(--radius-full);
  background: var(--color-bg-subtle);
  color: var(--color-text-subtle);
  font-size: var(--font-size-meta);
  font-weight: var(--font-weight-medium);
  line-height: 1;
}

:global(.subtab--active) .tabCount {
  background: var(--color-primary-soft);
  color: var(--color-primary);
}

.searchBox {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: 0 var(--space-3);
  height: 2.25rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-elevated);
  color: var(--color-text-subtle);
  flex: 0 0 auto;
  width: min(20rem, 100%);
}

.searchBox:focus-within {
  border-color: var(--color-primary);
}

.searchInput {
  flex: 1;
  border: none;
  background: transparent;
  color: var(--color-text);
  font: inherit;
  font-size: var(--font-size-body-sm);
  outline: none;
}

.searchInput::placeholder {
  color: var(--color-text-subtle);
}

/* ── Table ──────────────────────────────────────────────────── */

.table {
  width: 100%;
  border-collapse: collapse;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.table thead {
  background: var(--color-bg-subtle);
}

.table th,
.table td {
  text-align: left;
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--color-border);
  font-size: var(--font-size-body-sm);
}

.table th {
  font-size: var(--font-size-meta);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-subtle);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.table tbody tr:last-child td {
  border-bottom: 0;
}

.table tbody tr:hover {
  background: var(--color-bg-subtle);
}

/* Cards móviles — ocultas en desktop */
.cardList {
  display: none;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: var(--space-3);
}

.card {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
}

.cardTop {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
}

.cardIdentity {
  min-width: 0;
}

.cardMeta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  font-size: var(--font-size-body-sm);
}

.cardActions {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--space-2);
}

.cardActions .enterButton {
  justify-content: center;
  min-height: 2.75rem; /* target táctil */
  width: 100%;
}

.configButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 2.75rem;
  width: 100%;
  padding: 6px var(--space-3);
  border: 1px solid var(--color-border);
  background: transparent;
  color: var(--color-text);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.configButton:hover {
  background: var(--color-bg-subtle);
}

/* Móvil: ocultar tabla, mostrar cards.
   Debe ir DESPUÉS de .cardList { display: none } o el cascade lo tapa
   (bug aac19ae: el media query se borró en vez de moverse). */
@media (max-width: 720px) {
  .page {
    padding: 0;
    gap: var(--space-4);
    padding-bottom: max(var(--space-4), calc(env(safe-area-inset-bottom, 0px) + 5rem));
  }

  .toolbar {
    flex-direction: column;
    align-items: stretch;
    gap: var(--space-3);
    margin-bottom: 0;
  }

  .searchBox {
    width: 100%;
  }

  .tableDesktop {
    display: none;
  }

  .cardList {
    display: flex;
    flex-direction: column;
  }

  .toolbar :global(.subtabs) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }

  .toolbar :global(.subtabs)::-webkit-scrollbar {
    display: none;
  }
}

.tenantCell {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.tenantCell__icon {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-sm);
  background: var(--color-bg-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-subtle);
}

.tenantCell__name {
  font-weight: var(--font-weight-medium);
}

.tenantCell__domain {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-meta);
  color: var(--color-text-subtle);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.tenantCell__slug {
  font-size: var(--font-size-meta);
  color: var(--color-text-subtle);
}

.tenantCell__slug code {
  font-family: var(--font-mono);
  font-size: 0.95em;
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: var(--font-size-meta);
  font-weight: var(--font-weight-medium);
  text-transform: lowercase;
  white-space: nowrap;
  cursor: help;
}

/* Editor de capacidades (modal tenant) */
.capGrid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 16px;
}

.capToggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: var(--font-size-body-sm);
  color: var(--color-text);
  cursor: pointer;
}

/* Tooltip de capacidades sobre el badge de plan (conciso, una línea que wrappea) */
.planTip {
  text-align: left;
  max-width: 240px;
  line-height: 1.5;
}

.badge__crm-base {
  background: var(--color-primary-soft);
  color: var(--color-primary);
}

.badge__web-cms {
  background: color-mix(in srgb, var(--color-primary) 12%, transparent);
  color: var(--color-primary);
}

.badge__sistema-focus {
  background: var(--color-warning-soft);
  color: var(--color-warning);
}

.badge__sistema-focus-ads {
  background: var(--color-success-soft);
  color: var(--color-success-strong);
}

.badge__custom {
  background: var(--color-bg-subtle);
  color: var(--color-text-muted);
  border: 1px dashed var(--color-border-strong);
}

.statusActive {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--color-success-strong);
  font-size: var(--font-size-meta);
}

.statusInactive {
  font-size: var(--font-size-meta);
  color: var(--color-text-subtle);
}

.cmsConfigured {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--color-success-strong);
  font-size: var(--font-size-meta);
}

.cmsNotConfigured {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--color-text-subtle);
  font-size: var(--font-size-meta);
}

/* ── Modal ──────────────────────────────────────────────────── */

.modalBackdrop {
  position: fixed;
  inset: 0;
  background: var(--color-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: var(--space-4);
}

.modal {
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--color-border);
}

.modal__header h2 {
  margin: 0;
  font-size: var(--font-size-h3);
  font-weight: var(--font-weight-semibold);
}

.modal__body {
  padding: var(--space-5);
  display: grid;
  gap: var(--space-5);
  overflow-y: auto;
}

.modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-5);
  border-top: 1px solid var(--color-border);
}

.modal__footerRight {
  display: flex;
  gap: var(--space-2);
  margin-left: auto;
}

/* ── Sections / fieldsets ────────────────────────────────────── */

.section {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-4);
  margin: 0;
  display: grid;
  gap: var(--space-3);
}

.section legend {
  padding: 0 var(--space-2);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-semibold);
}

.sectionHint {
  margin: 0;
  font-size: var(--font-size-meta);
  color: var(--color-text-subtle);
}

.helpCallout {
  margin: 0 0 var(--space-3) 0;
  padding: var(--space-3) var(--space-4);
  background: var(--color-bg-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm, 6px);
  font-size: var(--font-size-meta);
  color: var(--color-text-subtle);
  line-height: 1.5;
}
.helpCallout p {
  margin: 0 0 var(--space-2) 0;
}
.helpCallout p:last-child {
  margin-bottom: 0;
}
.helpCallout strong {
  color: var(--color-text);
}
.helpCallout ol {
  margin: var(--space-2) 0 0 0;
  padding-left: 1.2em;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.helpCallout code {
  padding: 1px 4px;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: 3px;
  font-size: 0.95em;
}
.helpCallout a {
  color: var(--color-primary);
  text-decoration: underline;
}

.row2 {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--space-3);
}

/* ── PAT input + Probar ─────────────────────────────────────── */

.patRow {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--space-2);
  align-items: start;
}

.patBtn {
  height: 36px;
  padding: 0 var(--space-3);
  border: 1px solid var(--color-border);
  background: var(--color-bg-elevated);
  color: var(--color-text);
  border-radius: var(--radius-md, 6px);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
  white-space: nowrap;
  transition: background 120ms ease;
}

.patBtn:hover {
  background: var(--color-bg-subtle);
}

.patBtn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.patStatus {
  font-size: var(--font-size-body-xs, 12px);
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.patStatusOk {
  color: var(--color-success-strong);
}

.patStatusErr {
  color: var(--color-danger-strong);
}

.patStatusInfo {
  color: var(--color-text-subtle);
}

/* ── Field ──────────────────────────────────────────────────── */

.field {
  display: grid;
  gap: 4px;
}

.field__label {
  font-size: var(--font-size-meta);
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.field__tipIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--color-bg-subtle);
  color: var(--color-text-subtle);
  cursor: help;
  transition: color var(--duration-base) var(--ease-standard);
}

.field__tipIcon:hover {
  color: var(--color-text);
}

.field__required {
  color: var(--color-danger);
  margin-left: 4px;
}

.field__hint {
  font-size: var(--font-size-meta);
  color: var(--color-text-subtle);
}

.input,
.select {
  font: inherit;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg);
  color: var(--color-text);
}

.input:focus,
.select:focus {
  outline: 2px solid var(--color-primary);
  outline-offset: 0;
  border-color: transparent;
}

.input:disabled {
  background: var(--color-bg-subtle);
  color: var(--color-text-subtle);
}

.checkbox {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.error {
  padding: var(--space-3);
  background: var(--color-danger-soft);
  border: 1px solid var(--color-danger-border);
  border-radius: var(--radius-md);
  color: var(--color-danger-strong);
  font-size: var(--font-size-body-sm);
}

/* ── Wizard ──────────────────────────────────────────────────── */

.modal__footerLeft {
  display: flex;
  gap: var(--space-2);
}

.wizardSteps {
  display: flex;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-5);
  border-bottom: 1px solid var(--color-border);
  background: var(--color-bg-subtle);
}

.wizardStep {
  flex: 1;
  text-align: center;
  padding: 6px var(--space-3);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-meta);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-subtle);
  background: transparent;
  border: 1px solid transparent;
}

.wizardStep__current {
  color: var(--color-text);
  background: var(--color-bg);
  border-color: var(--color-border);
}

.wizardStep__done {
  color: var(--color-success-strong);
}

.advanced {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);
}

.advanced__summary {
  cursor: pointer;
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-subtle);
}

.advanced__summary:hover {
  color: var(--color-text);
}

.advanced[open] .advanced__summary {
  color: var(--color-text);
  margin-bottom: var(--space-3);
}

.advanced__body {
  display: grid;
  gap: var(--space-3);
}

.passwordRow {
  display: flex;
  gap: var(--space-2);
  align-items: stretch;
}

.passwordRow .input {
  flex: 1;
  font-family: var(--font-mono);
}

/* ── Wizard final (paso "done") ──────────────────────────────── */

.doneBlock {
  display: grid;
  gap: var(--space-4);
}

.doneSummary {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--color-success-soft);
  border: 1px solid var(--color-success-border);
  border-radius: var(--radius-md);
}

.doneSummary__icon {
  color: var(--color-success-strong);
  flex-shrink: 0;
}

.doneSummary__title {
  font-size: var(--font-size-body);
  margin-bottom: 2px;
}

.doneSummary__sub {
  font-size: var(--font-size-meta);
  color: var(--color-text-subtle);
}

.doneSummary__sub code {
  font-family: var(--font-mono);
}

.credBox {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-4);
  display: grid;
  gap: var(--space-3);
  background: var(--color-bg-elevated);
}

.credBox__header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-meta);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-subtle);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.credRow {
  display: grid;
  gap: 4px;
}

.credRow__label {
  font-size: var(--font-size-meta);
  color: var(--color-text-subtle);
}

.credRow__valueWrap {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

.credRow__value,
.credRow__mono {
  flex: 1;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-bg);
  font-size: var(--font-size-body-sm);
  overflow-wrap: anywhere;
}

.credRow__mono {
  font-family: var(--font-mono);
}

.credRow__warn {
  font-size: var(--font-size-meta);
  color: var(--color-warning);
}

.notice {
  padding: var(--space-3) var(--space-4);
  background: var(--color-bg-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: var(--font-size-body-sm);
  color: var(--color-text-subtle);
}

/* ── Página de detalle del cliente (/clientes/$id) ──────────────────── */
.detailHeader {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
}
.backBtn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px var(--space-2);
  border: 0;
  background: transparent;
  color: var(--color-text-subtle);
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: var(--font-size-body-sm);
}
.backBtn:hover {
  color: var(--color-text);
  background: var(--color-bg-subtle);
}
.detailTitle {
  margin: 0;
  font-size: var(--font-size-h3);
  font-weight: 600;
  color: var(--color-text);
}
.detailSlug {
  font-size: var(--font-size-body-sm);
  color: var(--color-text-subtle);
  font-family: var(--font-mono, monospace);
}
.detailHeaderActions {
  margin-left: auto;
  display: flex;
  gap: var(--space-2);
}
.detailBody {
  display: grid;
  gap: var(--space-4);
}
.detailActions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin-top: var(--space-4);
  flex-wrap: wrap;
}
.detailActionsRight {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: var(--space-3);
}
.savedInline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-body-sm);
  color: var(--color-success-strong);
}
.spin {
  animation: clientes-spin 0.7s linear infinite;
}
@keyframes clientes-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Tarjetas de la página de detalle (consistentes con Chatbot/Integraciones) ── */
.detailCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  padding: var(--space-6);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-bg-elevated);
  margin: 0;
}
.detailCard + .detailCard {
  margin-top: var(--space-4);
}
.detailCardHead {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}
.detailCardTitle {
  font-size: var(--font-size-h5);
  font-weight: 600;
  margin: 0;
  color: var(--color-text);
}
.detailCardSub {
  font-size: var(--font-size-body-sm);
  color: var(--color-text-muted);
  margin: 0;
  max-width: 70ch;
}
/* Franja de campos dentro de una tarjeta de detalle. */
.detailFields {
  display: grid;
  gap: var(--space-4);
}
/* Sub-bloque tenue para agrupar un set de campos (ej. capacidades). */
.detailSub {
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-subtle);
}

/* Stack de tarjetas dentro de una pestaña (ej. Integraciones con varias). */
.detailStack {
  display: grid;
  gap: var(--space-4);
}

/* "Entrar como cliente" en el header del detalle: acción secundaria (outline),
   para que no compita con el botón primario "Guardar cambios" de cada pestaña. */
.enterButtonGhost {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px var(--space-3);
  border: 1px solid var(--color-border);
  background: transparent;
  color: var(--color-text);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  border-radius: var(--radius-md);
  cursor: pointer;
}
.enterButtonGhost:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.logoRow {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  flex-wrap: wrap;
}
.logoPreview {
  height: 48px;
  width: auto;
  max-width: 220px;
  object-fit: contain;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-bg);
  padding: 6px 10px;
}
.logoPlaceholder {
  width: 140px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  border: 1px dashed var(--color-border);
  font-size: var(--font-size-meta);
  color: var(--color-text-muted);
}
.logoActions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}
.logoBusy {
  opacity: 0.7;
  pointer-events: none;
}
.logoHint {
  margin: var(--space-2) 0 0;
  font-size: var(--font-size-meta);
  color: var(--color-text-muted);
}

.enterButtonGhost:hover {
  background: var(--color-bg-subtle);
  border-color: var(--color-text-subtle);
}

.resendCardList {
  max-width: 640px;
}

.resendCard .input {
  width: 100%;
  box-sizing: border-box;
}

.fichaHeader {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--space-3) var(--space-4);
  align-items: start;
  margin-bottom: var(--space-5);
  padding-bottom: var(--space-4);
  border-bottom: 1px solid var(--color-border);
}

.fichaBack {
  grid-column: 1 / -1;
  justify-self: start;
}

.fichaTitle {
  margin: 0;
  font-size: clamp(1.75rem, 1.2rem + 1.6vw, 2.25rem);
  font-weight: 650;
  letter-spacing: -0.02em;
  line-height: 1.15;
}

.fichaMeta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
}

.fichaChip,
.fichaChipOn,
.fichaChipOff {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--color-border);
  font-size: var(--font-size-meta, 0.75rem);
  font-weight: 600;
}

.fichaChipOn {
  color: var(--color-success-strong, var(--color-text));
  background: var(--color-success-soft);
}

.fichaChipOff {
  color: var(--color-danger);
  background: var(--color-danger-soft);
  border-color: var(--color-danger-border);
}

.fichaSlug {
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: var(--font-size-body-sm);
  color: var(--color-text-subtle);
}

.fichaActions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}

.fichaTabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  border-bottom: 1px solid var(--color-border);
  margin-bottom: var(--space-5);
}

.fichaTab,
.fichaTabActive {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 8px 14px;
  border: 0;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  background: transparent;
  color: var(--color-text-muted);
  font: inherit;
  font-size: var(--font-size-body-sm);
  font-weight: 600;
  cursor: pointer;
}

.fichaTab:hover {
  color: var(--color-text);
}

.fichaTabActive {
  color: var(--color-primary);
  border-bottom-color: var(--color-primary);
}

.sectionNav {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
}

.sectionNav a {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
  text-decoration: none;
}

.sectionNav a:hover {
  color: var(--color-text);
}

.implSection {
  display: grid;
  gap: var(--space-3);
  scroll-margin-top: 12px;
}

.implTitle {
  margin: 0;
  font-size: clamp(1.125rem, 1rem + 0.4vw, 1.375rem);
  font-weight: 650;
}

.actionRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: var(--space-4);
}

.offboardLink {
  border: 0;
  background: transparent;
  color: var(--color-text-subtle);
  font: inherit;
  font-size: var(--font-size-body-sm);
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  min-height: 44px;
  padding: 8px;
}

.offboardLink:hover {
  color: var(--color-text);
}

.offboardDialog {
  width: min(42rem, calc(100vw - 2rem));
  max-height: min(85vh, 48rem);
  overflow: auto;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
  background: var(--color-bg-elevated);
  color: var(--color-text);
}

.offboardDialog:modal {
  overscroll-behavior: contain;
}

.offboardDialog::backdrop {
  background: rgb(16 24 40 / 0.45);
  overflow: hidden;
  overscroll-behavior: contain;
}

.offboardDialogBar {
  display: flex;
  justify-content: flex-end;
}

.offboardClose {
  border: 0;
  background: transparent;
  color: var(--color-text-muted);
  font: inherit;
  cursor: pointer;
  min-height: 44px;
  padding: 8px;
}

@media (max-width: 720px) {
  .fichaHeader {
    grid-template-columns: 1fr;
  }

  .fichaActions {
    justify-content: flex-start;
  }
}
